<template>
  <div>
    <!-- Bulk Upload Sidebar -->
    <b-sidebar
      id="sidebar-student-bulk-upload"
      bg-variant="white"
      v-model="sidebarVisible"
      sidebar-class="sidebar-lg"
      right
      no-header
      backdrop
      shadow
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">{{ $t("Bulk Upload Students") }}</h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="sidebarVisible = false"
        />
      </div>

      <div class="p-2">
        <!-- Download Sample Button -->
        <!-- <b-button variant="outline-secondary" block @click="downloadSample('intermediate')">
          <feather-icon icon="DownloadIcon" class="mr-50" />
          {{ $t("Download Sample File") }}
        </b-button> -->

        <b-dropdown
          variant="outline-secondary"
          block
          no-caret
          menu-class="w-100"
        >
          <template #button-content>
            <feather-icon icon="DownloadIcon" class="mr-50" />
            <span>{{ $t("Download Sample File") }}</span>
          </template>
          <b-dropdown-item @click="downloadSample('intermediate')">
            {{ $t("Intermediate") }}
          </b-dropdown-item>
          <b-dropdown-item @click="downloadSample('utp')">
            {{ $t("UTP") }}
          </b-dropdown-item>
        </b-dropdown>

        <hr class="my-2" />

        <!-- Upload Excel File Button -->
        <b-button
          variant="primary"
          block
          @click="$refs.bulkFileInput.click()"
          :disabled="importLoading || !!jobID"
        >
          <feather-icon icon="UploadIcon" class="mr-50" />
          {{ $t("Upload Excel File") }}
        </b-button>
        <input
          ref="bulkFileInput"
          type="file"
          accept=".xlsx,.xls"
          style="display: none"
          @change="handleFileUpload"
        />

        <!-- Record count badge -->
        <div
          v-if="importData.length > 0"
          class="mt-2 d-flex align-items-center"
        >
          <b-badge variant="success" style="font-size: 14px; padding: 8px 16px">
            {{ importData.length }} {{ $t("records read from Excel") }}
          </b-badge>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="clearData()"
            v-b-tooltip.hover.top
            :title="$t('Clear')"
            class="btn-icon ml-1"
            :disabled="!!jobID"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>

        <!-- Validation errors -->
        <div v-if="validationErrors.length > 0" class="mt-2">
          <b-alert show variant="danger" class="p-1 mb-0">
            <div class="font-weight-bold mb-50">
              {{ validationErrors.length }}
              {{ $t("row(s) have validation errors") }}:
            </div>
            <ul
              class="mb-0 pl-1"
              style="max-height: 160px; overflow-y: auto; font-size: 12px"
            >
              <li v-for="(err, i) in validationErrors" :key="i">
                {{ $t("Row") }} {{ err.row }}: {{ err.message }}
              </li>
            </ul>
          </b-alert>
        </div>

        <!-- Import Now Button -->
        <b-button
          v-if="
            importData.length > 0 && !jobID && validationErrors.length === 0
          "
          variant="success"
          block
          class="mt-2"
          @click="importNow"
          :disabled="importLoading"
        >
          <b-spinner v-if="importLoading" small type="grow" class="mr-50" />
          <feather-icon v-else icon="CheckIcon" class="mr-50" />
          {{ $t("Import Now") }}
        </b-button>

        <!-- Bulk import job progress -->
        <div v-if="jobID" class="mt-2">
          <div class="font-weight-bold mb-50">
            {{
              jobStatus && jobStatus.percent >= 100
                ? $t("Completed")
                : $t("Processing...")
            }}
          </div>
          <b-progress
            v-if="jobStatus"
            v-model="jobProgressPercent"
            max="100"
            height="10px"
            :variant="jobProgressVariant"
            class="mb-50"
            show-progress
            animated
          />
          <div v-else class="small text-muted mb-50">
            {{ $t("Starting background job...") }}
          </div>
          <div v-if="jobStatus" class="small">
            {{ $t("Processed") }} {{ jobStatus.processed || 0 }} {{ $t("of") }}
            {{ jobStatus.total || 0 }}
            <span v-if="jobStatus.percent != null">
              ({{ Math.round(jobStatus.percent) }}%)
            </span>
          </div>
          <div
            v-if="jobStatus && jobStatus.error"
            class="small text-danger mt-50"
          >
            {{ jobStatus.error }}
          </div>
        </div>
      </div>
    </b-sidebar>

    <!-- Trigger Button -->
    <b-button
      variant="success"
      block
      @click="sidebarVisible = true"
      v-b-tooltip.hover.top
      :title="$t('Bulk Upload')"
      :class="buttonClass"
    >
      <feather-icon icon="DownloadIcon" />
      <!-- <span class="align-middle">{{ $t("Bulk Upload") }}</span> -->
    </b-button>
  </div>
</template>

<script>
import {
  BSidebar,
  BButton,
  BBadge,
  BProgress,
  BSpinner,
  BAlert,
  BDropdown,
  BDropdownItem,
  VBTooltip,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import * as XLSX from "xlsx";
import { mapActions } from "vuex";

export default {
  name: "StudentImport",
  props: {
    buttonClass: {
      type: String,
      default: "",
    },
  },
  components: {
    BSidebar,
    BButton,
    BBadge,
    BProgress,
    BSpinner,
    BAlert,
    BDropdown,
    BDropdownItem,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      sidebarVisible: false,
      importData: [],
      validationErrors: [],
      importLoading: false,
      jobID: null,
      jobStatus: null,
      pollTimer: null,
    };
  },
  computed: {
    jobProgressPercent() {
      if (!this.jobStatus || this.jobStatus.percent == null) return 0;
      const p = Number(this.jobStatus.percent);
      if (Number.isNaN(p)) return 0;
      return Math.min(100, Math.max(0, p));
    },
    jobProgressVariant() {
      if (!this.jobStatus) return "primary";
      if (this.jobStatus.error) return "danger";
      if (this.jobProgressPercent >= 100) return "success";
      return "primary";
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    clearData() {
      this.importData = [];
      this.validationErrors = [];
    },

    downloadSample(type = "intermediate") {
      const link = document.createElement("a");
      const fileName =
        type === "utp" ? "utp-student-sample.xlsx" : "student-sample.xlsx";
      link.href = `/${fileName}`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
          raw: false,
        });

        const getVal = (row, ...keys) => {
          for (const key of keys) {
            const found = Object.keys(row).find(
              (k) => k.trim().toLowerCase() === key.trim().toLowerCase()
            );
            if (
              found !== undefined &&
              row[found] !== undefined &&
              row[found] !== ""
            ) {
              return String(row[found]).trim();
            }
          }
          return "";
        };

        const toDecimal = (val) => {
          const cleaned =
            typeof val === "string" ? val.replace(/%/g, "").trim() : val;
          const n = parseFloat(cleaned);
          return isNaN(n) ? 0 : n;
        };

        const resolveStudentId = (val) => {
          if (val === null || val === undefined || val === "") return 0;
          const num = Number(val);
          return Number.isFinite(num) ? num : 0;
        };

        const toNum = (val) => {
          if (val === null || val === undefined || val === "") return 0;
          const num = Number(val);
          return Number.isFinite(num) ? num : 0;
        };

        const parsed = rows.map((row) => {
          const studentId = resolveStudentId(
            getVal(row, "id", "ID", "Id", "Student ID", "sID", "studentID")
          );

          const studentName = getVal(row, "Student Name", "Name", "name");
          const fatherName = getVal(
            row,
            "Father Name",
            "father_name",
            "Father"
          );
          const genderRaw = getVal(row, "Gender", "gender").toLowerCase();
          const gender =
            genderRaw === "male" || genderRaw === "m"
              ? "Male"
              : genderRaw === "female" || genderRaw === "f"
              ? "Female"
              : "";
          const contactStudent = getVal(
            row,
            "Student Contact No",
            "Student Contact No.",
            "Student Contact",
            "Student's Contact",
            "contact_student",
            "Contact (Student)",
            "Contact Student"
          );
          const contactFather = getVal(
            row,
            "WhatsApp No",
            "Whatsapp No",
            "Whatsapp No.",
            "contact_father",
            "Father Contact",
            "Contact Father",
            "Contact (Father)"
          );
          const contactMother = getVal(
            row,
            "Parent's Contact",
            "Parent's Contact No.",
            "Mother Contact",
            "contact_mother",
            "Contact Mother",
            "Contact (Mother)"
          );
          const className = getVal(row, "Class", "class_name", "Class Name");
          const sectionName = getVal(
            row,
            "Section",
            "section_name",
            "Section Name"
          );

          const branchCode = getVal(
            row,
            "Branch Code",
            "branchCode",
            "Branch code"
          );
          const scholarshipCategory = getVal(
            row,
            "Scholarship Category",
            "scholarshipCategory",
            "Scholarship"
          );
          const email = getVal(row, "Email Address", "Email");
          const stdCategory = getVal(
            row,
            "Student Category",
            "Student's Category",
            "StudentCategory"
          );

          return {
            student: {
              id: studentId,
              name: studentName,
              father_name: fatherName,
              gender,
              contact_student: contactStudent,
              contact_father: contactFather,
              contact_mother: contactMother,
              class_name: className,
              section_name: sectionName,
              emailAddress: email,
              last_result: stdCategory,
            },
            studentDetails: {
              id: 0,
              sID: studentId,
              campusID: this.$store.state.userData.cId || 0,
              region: getVal(row, "Region", "region"),
              area: getVal(row, "Area", "area"),
              federalRegNo: getVal(
                row,
                "Federal Board Registration No",
                "Federal Reg No",
                "federalRegNo",
                "Federal Registration No"
              ),
              matricRollNo: getVal(
                row,
                "Matric Roll No.",
                "Matric Roll No",
                "matricRollNo",
                "Matric Roll Number"
              ),
              school: getVal(row, "School", "school"),
              matricBoard: getVal(
                row,
                "Matric Board",
                "matricBoard",
                "Matriculation Board"
              ),
              ninthPerc: toDecimal(
                getVal(row, "9th %", "ninthPerc", "9th Percentage")
              ),
              hsstPerc: toDecimal(
                getVal(row, "HSST %", "hsstPerc", "HSST Percentage")
              ),
              overallPerc: toDecimal(
                getVal(
                  row,
                  "Overall Score (HSST + Matric)",
                  "Overall Score (Matric + HSST)",
                  "Overall %",
                  "overallPerc",
                  "Overall Percentage"
                )
              ),
              hsstStatus: getVal(row, "HSST Status", "hsstStatus"),
              matricPerc: toDecimal(
                getVal(row, "Matric %", "matricPerc", "Matric Percentage")
              ),
              matricGrade: getVal(row, "Matric Grade", "matricGrade"),
              studentCnic: getVal(
                row,
                "Student's CNIC No.",
                "Student's CNIC No",
                "studentCnic",
                "Student CNIC",
                "Student CNIC No"
              ),
              scholarshipCategory,
              instituteName: getVal(
                row,
                "Institute Name",
                "instituteName",
                "Institute"
              ),
              branchCode,
              comment: getVal(row, "Comment", "comment", "Comments"),
              scholarshipStatus:
                getVal(row, "Scholarship Status", "scholarshipStatus") || "N/A",
              studentStatus:
                getVal(row, "Student Status", "studentStatus") || "N/A",
              avgAttendance: toDecimal(
                getVal(
                  row,
                  "Avg Attendance",
                  "avgAttendance",
                  "Average Attendance"
                )
              ),
              interFaculty: getVal(
                row,
                "Inter Faculty",
                "interFaculty",
                "Intermediate Faculty"
              ),
              firstYearRollNo: getVal(
                row,
                "1st Year Roll Number",
                "1st Year Roll No",
                "First Year Roll No",
                "firstYearRollNo"
              ),
              intermediateBoard: getVal(
                row,
                "Intermediate Board",
                "intermediateBoard",
                "Inter Board"
              ),
              firstYearPerc: toDecimal(
                getVal(
                  row,
                  "1st Year % ",
                  "1st Year %",
                  "First Year %",
                  "firstYearPerc",
                  "1st Year Percentage"
                )
              ),
              paperPassed: getVal(
                row,
                "1st Year Paper Passed",
                "Paper Passed",
                "paperPassed"
              ),
              secondYearRollNo: getVal(
                row,
                "Second Year Roll No",
                "secondYearRollNo",
                "2nd Year Roll No",
                "2nd Year Roll Number"
              ),
              secondYearPerc: toDecimal(
                getVal(
                  row,
                  "Second Year %",
                  "secondYearPerc",
                  "2nd Year %",
                  "2nd Year Percentage"
                )
              ),
              college: getVal(row, "College", "College Name"),
              fiscalYear: getVal(row, "Fiscal Year", "FY"),
              schoolID: getVal(row, "School ID", "SchoolID"),
              supportedYears: toNum(
                getVal(row, "Supported Years", "supportedYears")
              ),
              // utp fields
              utpSupport: getVal(row, "UTP Support", "utpSupport"),
              utpFiscalYear: getVal(row, "UTP Fiscal Year", "utpFiscalYear"),
              utpCourse: getVal(row, "UTP Course", "utpCourse"),
              utpLockedProgram1: getVal(
                row,
                "1. Locked Program",
                "Locked Program 1",
                "utpLockedProgram1",
                "1 Locked Program"
              ),
              utpLockedTier1: getVal(
                row,
                "1. Locked Tier",
                "Locked Tier 1",
                "utpLockedTier1",
                "1 Locked Tier"
              ),
              utpLockedProgram2: getVal(
                row,
                "2. Locked Program",
                "Locked Program 2",
                "utpLockedProgram2",
                "2 Locked Program"
              ),
              utpLockedTier2: getVal(
                row,
                "2. Locked Tier",
                "Locked Tier 2",
                "utpLockedTier2",
                "2 Locked Tier"
              ),
              utpLockedProgram3: getVal(
                row,
                "3. Locked Program",
                "Locked Program 3",
                "utpLockedProgram3",
                "3 Locked Program"
              ),
              utpLockedTier3: getVal(
                row,
                "3. Locked Tier",
                "Locked Tier 3",
                "utpLockedTier3",
                "3 Locked Tier"
              ),
              utpInstituteID: getVal(
                row,
                "UTP Institute ID",
                "utpInstituteID",
                "UTP InstituteId"
              ),
              utpInstituteName: getVal(
                row,
                "UTP Institute",
                "utpInstituteName",
                "UTP Institute Name"
              ),
              utpClassMode: getVal(
                row,
                "Mode of Class",
                "utpClassMode",
                "Class Mode"
              ),
              utpQarzehasnaStatus: getVal(
                row,
                "Qarz-e-hasna Form Status",
                "utpQarzehasnaStatus",
                "Qarz-e-hasna Status"
              ),
            },
          };
        });

        // Validate required fields
        const errors = [];
        parsed.forEach((item, idx) => {
          const s = item.student;
          const sd = item.studentDetails;
          const missing = [];

          if (!s.name) missing.push(this.$t("Name"));
          if (!s.father_name) missing.push(this.$t("Father Name"));
          if (!s.class_name) missing.push(this.$t("Class"));
          if (!s.contact_student) missing.push(this.$t("Student Contact"));
          if (!s.contact_father) missing.push(this.$t("WhatsApp No"));
          if (!sd.branchCode) missing.push(this.$t("Branch Code"));
          if (!sd.scholarshipCategory)
            missing.push(this.$t("Scholarship Category"));

          if (missing.length > 0) {
            errors.push({
              row: idx + 2,
              message: `${this.$t("Missing")}: ${missing.join(", ")}`,
            });
          }
        });

        this.validationErrors = errors;

        this.importData = parsed.filter(
          (item) =>
            item.student.name !== "" ||
            item.student.father_name !== "" ||
            item.student.class_name !== ""
        );

        this.$bvToast.toast(
          `${this.importData.length} ${this.$t("records read from Excel")}${
            errors.length > 0
              ? ` — ${errors.length} ${this.$t(
                  "row(s) have validation errors"
                )}`
              : ""
          }`,
          {
            title: this.$t("File Loaded"),
            variant: errors.length > 0 ? "warning" : "info",
            toaster: "b-toaster-top-center",
          }
        );
      };

      reader.readAsArrayBuffer(file);
      this.$refs.bulkFileInput.value = "";
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.importLoading = false;
    },

    async startPolling() {
      this.stopPolling();
      await this.pollJobStatus();
      this.pollTimer = setInterval(() => {
        this.pollJobStatus();
      }, 2000);
    },

    async pollJobStatus() {
      if (!this.jobID) return;
      const res = await this.get({
        url:
          this.$store.state.domain +
          "BackgroundJobs/GetJobStatus/" +
          this.jobID +
          "?db=" +
          this.$store.state.userData.db,
        token: this.$store.state.userData.token,
        returnData: true,
        context: this,
      });
      console.log("job status response ", res);

      if (res === false || res == null) {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.stopPolling();
        this.jobID = null;
        this.jobStatus = null;
        return;
      }

      if (res.status === "error" || res.status === "failed") {
        this.jobStatus = res;
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.stopPolling();
        this.jobID = null;
        this.jobStatus = null;
        return;
      }

      if (res.status === "notFound") {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        this.stopPolling();
        this.jobID = null;
        this.jobStatus = null;
        return;
      }

      this.jobStatus = res;
      const pct = Number(res.percent);
      const total = Number(res.total);
      const processed = Number(res.processed);
      const done =
        res.status === "succeeded" ||
        (!Number.isNaN(pct) && pct >= 100) ||
        (total > 0 && !Number.isNaN(processed) && processed >= total);

      if (done) {
        this.$bvToast.toast(this.$t("Students imported successfully."), {
          title: this.$t("Success"),
          variant: "success",
          toaster: "b-toaster-top-center",
        });
        this.stopPolling();
        this.jobID = null;
        this.jobStatus = null;
        this.importData = [];
        this.validationErrors = [];
        this.sidebarVisible = false;
        this.$emit("imported");
      }
    },

    async importNow() {
      if (this.importData.length === 0) return;
      console.log("import data", this.importData);

      this.importLoading = true;

      const response = await this.post({
        url:
          this.$store.state.domain +
          "StudentDetails/BulkUpload?db=" +
          this.$store.state.userData.db +
          "&userID=" +
          this.$store.state.userData.userID,
        body: this.importData,
        context: this,
        isBulkUpload: true,
        token: this.$store.state.userData.token,
      });

      this.importLoading = false;

      if (!response) {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      if (response.status === "error") {
        let invalid = 0;
        if (Array.isArray(response.data)) {
          invalid = response.data.length;
        }

        const baseMsg = this.$t("Validation failed.");
        const msg =
          invalid > 0
            ? `${baseMsg} ${invalid} ${this.$t("invalid records")}`
            : baseMsg;

        this.$bvToast.toast(msg, {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      const jid =
        response.jobID ||
        response.jobId ||
        (response.data && (response.data.jobID || response.data.jobId));

      if (!jid) {
        this.$bvToast.toast(this.$t("Upload failed."), {
          title: this.$t("Error") + "!",
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      this.jobID = jid;
      this.jobStatus = null;
      this.startPolling();
    },
  },
};
</script>
